/**
 * compressionUtils - Compression utilities for offline queue data
 * Uses LZ-string for efficient localStorage compression
 */
import LZString from 'lz-string';

/**
 * Compress data using LZ-string
 * @param {any} data - Data to compress
 * @returns {string} Compressed string
 */
export const compressData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    const compressed = LZString?.compress(jsonString);
    return compressed;
  } catch (error) {
    console.error('[Compression] Failed to compress data:', error);
    return JSON.stringify(data); // Fallback to uncompressed
  }
};

/**
 * Decompress data using LZ-string
 * @param {string} compressed - Compressed string
 * @returns {any} Decompressed data
 */
export const decompressData = (compressed) => {
  try {
    const decompressed = LZString?.decompress(compressed);
    return JSON.parse(decompressed);
  } catch (error) {
    console.error('[Compression] Failed to decompress data:', error);
    // Try parsing as uncompressed JSON (fallback)
    try {
      return JSON.parse(compressed);
    } catch (e) {
      return null;
    }
  }
};

/**
 * Compress large text fields (comments, story content)
 * @param {string} text - Text to compress
 * @returns {object} Object with compressed text and flag
 */
export const compressText = (text) => {
  if (!text || text?.length < 100) {
    // Don't compress short text
    return { text, isCompressed: false };
  }

  try {
    const compressed = LZString?.compressToUTF16(text);
    const compressionRatio = compressed?.length / text?.length;
    
    // Only use compression if it saves at least 20% space
    if (compressionRatio < 0.8) {
      return { text: compressed, isCompressed: true };
    }
    
    return { text, isCompressed: false };
  } catch (error) {
    console.error('[Compression] Failed to compress text:', error);
    return { text, isCompressed: false };
  }
};

/**
 * Decompress text field
 * @param {string} text - Text to decompress
 * @param {boolean} isCompressed - Whether text is compressed
 * @returns {string} Decompressed text
 */
export const decompressText = (text, isCompressed) => {
  if (!isCompressed) {
    return text;
  }

  try {
    return LZString?.decompressFromUTF16(text);
  } catch (error) {
    console.error('[Compression] Failed to decompress text:', error);
    return text; // Return as-is if decompression fails
  }
};

/**
 * Calculate compression statistics
 * @param {any} original - Original data
 * @param {string} compressed - Compressed data
 * @returns {object} Compression stats
 */
export const getCompressionStats = (original, compressed) => {
  const originalSize = JSON.stringify(original)?.length;
  const compressedSize = compressed?.length;
  const ratio = compressedSize / originalSize;
  const savedBytes = originalSize - compressedSize;
  const savedPercent = ((1 - ratio) * 100)?.toFixed(1);

  return {
    originalSize,
    compressedSize,
    ratio,
    savedBytes,
    savedPercent,
  };
};
